import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { usePersistedFilters } from '../../hooks/usePersistedFilters';
import { Download, Filter, Loader, RefreshCcw, Search, Upload, X } from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import api from '../../api';
import Alert from '../../components/Alert/Alert';
import AdvancedFilterPanel, { FilterButton } from '../../components/common/AdvancedFilter';
import FilterAwarenessBar from '../../components/common/AdvancedFilter/FilterAwarenessBar';
import { getActivePeriodRange, getActiveFilterCount, DEFAULT_PERIOD_STATE } from '../../components/common/AdvancedFilter/periodUtils';
import CertificatePreview from '../../components/CertificatePreview';
import { useAuth } from '../../contexts/AuthProvider';
import CertificateLifecycleHealthCard from '../../components/audit/CertificateLifecycleHealthCard';
import {
  resolveLifecycleMeta,
  resolveStatusMeta,
  toCertificateDate as toDate,
} from '../../utils/certificateLifecycleUtils';

const AUDIT_MODULE = 'audit';
const STANDARD_OPTIONS = ['ISO', 'ISCC', 'LVV', 'LATIK'];
const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'EXPIRING_SOON', label: 'Expiring Soon' },
  { value: 'EXPIRED', label: 'Expired' },
];
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DEFAULT_FILTERS = {
  ...DEFAULT_PERIOD_STATE,
  standard: '',
  status: '',
  lifecycleStatus: '',
  dateFrom: '',
  dateTo: '',
  search: '',
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

export { resolveStatusMeta } from '../../utils/certificateLifecycleUtils';

export const buildClientName = (certificate = {}) => (
  certificate.clientName
  || certificate.companyName
  || certificate.project?.clientName
  || certificate.project?.companyName
  || certificate.client?.name
  || certificate.company?.name
  || '—'
);

export const buildStandardLabel = (certificate = {}) => {
  if (Array.isArray(certificate.standards) && certificate.standards.length > 0) {
    return certificate.standards.filter(Boolean).join(', ');
  }
  return certificate.standard
    || certificate.standardName
    || certificate.project?.standard
    || certificate.project?.standardName
    || '—';
};

export const buildCertificateNumber = (certificate = {}) => (
  certificate.certificateNumber
  || certificate.certificateNo
  || certificate.number
  || certificate.certNumber
  || '—'
);

export const resolveProjectDetailId = (certificate = {}) => (
  certificate.leadId
  || certificate.projectLeadId
  || certificate.project?.leadId
  || certificate.project?.lead?.id
  || certificate.project?.lead?.leadId
  || certificate.project?.lead
  || null
);

export const resolveClientDetailId = (certificate = {}) => {
  // Treat 0 / "0" / "" as missing so legacy AuditQ rows with empty client mapping
  // do not render a broken link to /audit/clients/0.
  const candidates = [
    certificate.clientId,
    certificate.companyId,
    certificate.project?.clientId,
    certificate.project?.companyId,
    certificate.client?.id,
    certificate.company?.id,
  ];
  for (const value of candidates) {
    if (value == null) continue;
    const asNumber = Number(value);
    if (Number.isFinite(asNumber) && asNumber > 0) {
      return asNumber;
    }
  }
  return null;
};

export const matchesClientFilters = (certificate, filters) => {
  const searchNeedle = String(filters.search || '').trim().toLowerCase();
  const certificateStatus = resolveStatusMeta(certificate).value;
  const standardLabel = buildStandardLabel(certificate).toUpperCase();
  const issueDate = toDate(certificate.issueDate || certificate.validFrom || certificate.createdAt);

  if (filters.standard && !standardLabel.includes(filters.standard.toUpperCase())) {
    return false;
  }

  if (filters.status && certificateStatus !== filters.status) {
    return false;
  }

  if (filters.lifecycleStatus && certificate.lifecycleStatus !== filters.lifecycleStatus) {
    return false;
  }

  if (filters.dateFrom) {
    const fromDate = toDate(filters.dateFrom);
    if (fromDate && issueDate && issueDate < fromDate) {
      return false;
    }
  }

  if (filters.dateTo) {
    const toDateValue = toDate(filters.dateTo);
    if (toDateValue && issueDate && issueDate > toDateValue) {
      return false;
    }
  }

  if (!searchNeedle) return true;

  const haystacks = [buildClientName(certificate), buildCertificateNumber(certificate)]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return haystacks.includes(searchNeedle);
};

export const normalizeListResponse = (payload, pageSize) => {
  const root = payload?.data ?? payload ?? {};
  const container = Array.isArray(root)
    ? { content: root, totalElements: root.length, totalPages: 1 }
    : Array.isArray(root?.content)
      ? root
      : Array.isArray(root?.data?.content)
        ? root.data
        : Array.isArray(root?.items)
          ? { content: root.items, totalElements: root.total ?? root.items.length, totalPages: root.totalPages }
          : Array.isArray(root?.data)
            ? { content: root.data, totalElements: root.total ?? root.data.length, totalPages: root.totalPages }
            : { content: [] };

  const items = Array.isArray(container.content) ? container.content : [];
  const totalElements = Number(container.totalElements ?? container.total ?? items.length) || items.length;
  const totalPages = Number(container.totalPages ?? Math.max(1, Math.ceil(totalElements / (container.size || pageSize || 1)))) || 1;

  return {
    items,
    totalElements,
    totalPages,
  };
};

export const buildPageItems = (currentPage, totalPages) => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = [1];
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  if (start > 2) pages.push('left-ellipsis');
  for (let page = start; page <= end; page += 1) pages.push(page);
  if (end < totalPages - 1) pages.push('right-ellipsis');
  pages.push(totalPages);

  return pages;
};

const rowsFromDump = (rawText) => {
  if (!rawText || !String(rawText).trim()) return [];
  const parsed = JSON.parse(rawText);
  if (Array.isArray(parsed)) return parsed;
  if (Array.isArray(parsed?.data)) return parsed.data;
  return [];
};

// Strips the auditqv2-main local static dump (public/iso-certificate.json) down to only the
// fields the backend migration endpoint needs — drops the unused DataTables "action" HTML
// blob and internal ids so the upload payload stays well under any reverse-proxy body limit.
export const slimIsoJsonRows = (rawText) => rowsFromDump(rawText).map((row) => ({
  id: row.id,
  certificate_number: row.certificate_number,
  client: row.client,
  standard: row.standard,
  initial_date: row.initial_date,
  issue_date: row.issue_date,
  expired_date: row.expired_date,
  latest_certification_date: row.latest_certification_date,
  is_approve: row.is_approve,
  is_iscc: row.is_iscc,
}));

// Same as slimIsoJsonRows but for the ISCC dump (public/iscc-certificate.json), which sources
// issue/expiry dates from iscc_valid_from_start_date/iscc_valid_from_end_date instead.
export const slimIsccJsonRows = (rawText) => rowsFromDump(rawText).map((row) => ({
  id: row.id,
  certificate_number: row.certificate_number,
  client: row.client,
  standard: row.standard,
  // valid_from is what auditqv2's own "Certificate ISCC" monitoring page displays as the
  // validity range (e.g. "10-11-2025<br>Until<br>09-11-2026") — the backend treats it as the
  // primary date source for ISCC/Sustainability, falling back to the iscc_valid_from_*_date
  // fields only when it's missing.
  valid_from: row.valid_from,
  iscc_valid_from_start_date: row.iscc_valid_from_start_date,
  iscc_valid_from_end_date: row.iscc_valid_from_end_date,
  is_approve: row.is_approve,
  is_iscc: row.is_iscc,
}));

export const buildRequestParams = (filters, page, size, includeSearch = true) => {
  const params = {
    page: Math.max(0, page - 1),
    size,
  };

  if (filters.standard) params.standard = filters.standard;
  if (filters.status) params.status = filters.status;
  if (filters.lifecycleStatus) params.lifecycleStatus = filters.lifecycleStatus;
  if (filters.dateFrom) params.dateFrom = filters.dateFrom;
  if (filters.dateTo) params.dateTo = filters.dateTo;
  if (includeSearch && filters.search) params.search = filters.search.trim();

  return params;
};

const CertificateMonitoring = () => {
  const navigate = useNavigate();
  const alertTimerRef = useRef(null);
  const tableSectionRef = useRef(null);
  const { loadPersistedState, persistState } = usePersistedFilters('certificate_monitoring');
  const { can, hasRole, capabilitiesLoading } = useAuth();
  const isAdmin = hasRole('ROLE_ADMIN');
  const isDirector = hasRole('ROLE_DIRECTOR');
  const canViewPage =
    isAdmin
    || isDirector
    || can('view_own', AUDIT_MODULE)
    || can('view_department', AUDIT_MODULE)
    || can('view_global', AUDIT_MODULE);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState('');
  const [alert, setAlert] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showJsonImportModal, setShowJsonImportModal] = useState(false);
  const [jsonImporting, setJsonImporting] = useState(false);
  const [isoFile, setIsoFile] = useState(null);
  const [isccFile, setIsccFile] = useState(null);
  const [auditqConfigured, setAuditqConfigured] = useState(null);
  const [selectedCert, setSelectedCert] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalElements: 0,
    totalPages: 1,
  });
  const [healthStats, setHealthStats] = useState(null);

  const showAlert = useCallback((type, message) => {
    setAlert({ type, message });
    if (alertTimerRef.current) {
      window.clearTimeout(alertTimerRef.current);
    }
    alertTimerRef.current = window.setTimeout(() => setAlert(null), 4000);
  }, []);

  useEffect(() => () => {
    if (alertTimerRef.current) {
      window.clearTimeout(alertTimerRef.current);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    api.get('/audit/certificates/auditq-status')
      .then(res => setAuditqConfigured(res.data?.configured === true))
      .catch(() => setAuditqConfigured(false));
  }, [isAdmin]);

  useEffect(() => {
    api.get('/audit/dashboard/cert-competency-stats')
      .then(res => setHealthStats(res.data?.certificates || null))
      .catch(() => {});
  }, []);

  // Restore persisted filter state on mount
  useEffect(() => {
    const saved = loadPersistedState();
    if (saved) {
      const restoredFilters = saved.filters ?? DEFAULT_FILTERS;
      const restoredPage = saved.page ?? 0;
      const restoredSize = saved.size ?? 10;
      setFilters(restoredFilters);
      setAppliedFilters(restoredFilters);
      setPagination(prev => ({ ...prev, currentPage: restoredPage + 1, pageSize: restoredSize }));
    }
  // eslint-disable-next-line
  }, []);

  const fetchCertificates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/audit/certificates', {
        params: buildRequestParams(appliedFilters, pagination.currentPage, pagination.pageSize),
      });

      const normalized = normalizeListResponse(response.data, pagination.pageSize);
      const filteredItems = normalized.items.filter((item) => matchesClientFilters(item, appliedFilters));
      const shouldUseFallbackTotals = normalized.items.length === normalized.totalElements;

      setCertificates(filteredItems);
      setPagination((prev) => ({
        ...prev,
        totalElements: shouldUseFallbackTotals ? filteredItems.length : normalized.totalElements,
        totalPages: shouldUseFallbackTotals
          ? Math.max(1, Math.ceil(filteredItems.length / (prev.pageSize || 1)))
          : Math.max(1, normalized.totalPages),
      }));
    } catch (error) {
      console.error('Failed to load certificates:', error);
      setCertificates([]);
      setPagination((prev) => ({ ...prev, totalElements: 0, totalPages: 1 }));
      showAlert('danger', error?.response?.data?.message || 'Failed to load certificates.');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, pagination.currentPage, pagination.pageSize, showAlert]);

  useEffect(() => {
    if (capabilitiesLoading || !canViewPage) return;
    fetchCertificates();
  }, [capabilitiesLoading, canViewPage, fetchCertificates]);

  const pageItems = useMemo(
    () => buildPageItems(pagination.currentPage, pagination.totalPages),
    [pagination.currentPage, pagination.totalPages],
  );

  const filterFields = useMemo(() => [
    {
      key: 'standard',
      label: 'Standard',
      type: 'select',
      options: STANDARD_OPTIONS.map((standard) => ({ value: standard, label: standard })),
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      options: STATUS_OPTIONS,
    },
    {
      key: 'issueDate',
      label: 'Issue Date',
      type: 'dateRange',
      fromKey: 'dateFrom',
      toKey: 'dateTo',
    },
  ], []);

  const activeCount = getActiveFilterCount(appliedFilters, filterFields);

  const filterTags = useMemo(() => {
    const tags = [];

    if (appliedFilters.period) {
      tags.push({ key: 'period', label: `Period: ${appliedFilters.period.replace(/_/g, ' ')}` });
    } else if (appliedFilters.dateFrom || appliedFilters.dateTo) {
      const label = [appliedFilters.dateFrom, appliedFilters.dateTo].filter(Boolean).join(' → ');
      tags.push({ key: 'issueDate', label: `Issue Date: ${label}` });
    }

    if (appliedFilters.standard) tags.push({ key: 'standard', label: `Standard: ${appliedFilters.standard}` });
    if (appliedFilters.status) tags.push({ key: 'status', label: `Status: ${appliedFilters.status}` });
    if (appliedFilters.lifecycleStatus) {
      tags.push({ key: 'lifecycleStatus', label: `Lifecycle Status: ${appliedFilters.lifecycleStatus}` });
    }

    return tags;
  }, [appliedFilters]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleFilterApply = () => {
    const periodRange = getActivePeriodRange(filters);
    const resolved = {
      ...filters,
      dateFrom: periodRange.startDate || filters.dateFrom,
      dateTo: periodRange.endDate || filters.dateTo,
    };

    setAppliedFilters(resolved);
    setFilters(resolved);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    persistState({ filters: resolved, search: '', page: 0, size: pagination.pageSize });
  };

  const handleFilterReset = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    persistState({ filters: DEFAULT_FILTERS, search: '', page: 0, size: pagination.pageSize });
  };

  const handleRemoveFilter = (key) => {
    let updated;

    if (key === 'period' || key === 'issueDate') {
      updated = {
        ...appliedFilters,
        period: '',
        dateFrom: '',
        dateTo: '',
        startDate: '',
        endDate: '',
      };
    } else {
      updated = { ...appliedFilters, [key]: '' };
    }

    setAppliedFilters(updated);
    setFilters(updated);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    persistState({ filters: updated, search: '', page: 0, size: pagination.pageSize });
  };

  // Clicking a Certificate ISO/ISCC Status tile scopes the table to that exact standard +
  // lifecycle status combo (e.g. "ISCC / Valid") instead of just updating the summary card.
  const handleLifecycleTileClick = (standardScope, lifecycleStatusLabel) => {
    const updated = { ...appliedFilters, standard: standardScope, lifecycleStatus: lifecycleStatusLabel };
    setAppliedFilters(updated);
    setFilters(updated);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    persistState({ filters: updated, search: '', page: 0, size: pagination.pageSize });
    tableSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleExportData = async (type) => {
    setExporting(type);
    try {
      const response = await api.get('/audit/certificates', {
        params: {
          ...buildRequestParams(appliedFilters, 1, 1000),
          page: 0,
          size: 1000,
        },
      });

      const exportRows = normalizeListResponse(response.data, 1000).items
        .filter((item) => matchesClientFilters(item, appliedFilters));

      if (exportRows.length === 0) {
        showAlert('warning', 'There is no certificate data to export.');
        return;
      }

      const mappedRows = exportRows.map((certificate, index) => {
        const status = resolveStatusMeta(certificate);
        const lifecycle = resolveLifecycleMeta(certificate);
        return {
          No: index + 1,
          'Certificate Number': buildCertificateNumber(certificate),
          'Client Name': buildClientName(certificate),
          Standard: buildStandardLabel(certificate),
          'Issue Date': formatDate(certificate.issueDate || certificate.validFrom || certificate.createdAt),
          'Expiry Date': formatDate(certificate.expiryDate || certificate.expiredDate || certificate.validTo),
          'Lifecycle Status': lifecycle.label,
          'Health Status': status.label,
        };
      });

      if (type === 'excel') {
        const worksheet = XLSX.utils.json_to_sheet(mappedRows);
        worksheet['!cols'] = [
          { wch: 8 },
          { wch: 24 },
          { wch: 28 },
          { wch: 20 },
          { wch: 16 },
          { wch: 16 },
          { wch: 18 },
          { wch: 18 },
        ];
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Certificates');
        XLSX.writeFile(workbook, `certificate_monitoring_${new Date().toISOString().slice(0, 10)}.xlsx`);
      } else {
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
        doc.setFontSize(16);
        doc.text('Certificate Monitoring', 14, 16);
        doc.setFontSize(10);
        doc.text(`Generated on ${new Date().toLocaleString()}`, 14, 23);

        autoTable(doc, {
          startY: 30,
          head: [['No', 'Certificate Number', 'Client Name', 'Standard', 'Issue Date', 'Expiry Date', 'Status']],
          body: mappedRows.map((row) => Object.values(row)),
          styles: { fontSize: 8, cellPadding: 2.5 },
          headStyles: { fillColor: [26, 118, 126], textColor: 255 },
          margin: { left: 14, right: 14 },
        });

        doc.save(`certificate_monitoring_${new Date().toISOString().slice(0, 10)}.pdf`);
      }

      showAlert('success', `Certificate data exported to ${type.toUpperCase()} successfully.`);
    } catch (error) {
      console.error(`Failed to export ${type}:`, error);
      showAlert('danger', error?.response?.data?.message || `Failed to export ${type.toUpperCase()}.`);
    } finally {
      setExporting('');
    }
  };

  const handleImportFromAuditq = async () => {
    setShowImportConfirm(false);
    setImporting(true);
    setImportResult(null);
    try {
      const response = await api.post('/audit/certificates/import-from-auditq');
      setImportResult({ success: true, ...response.data });
      fetchCertificates();
    } catch (error) {
      const msg = error?.response?.data?.message || 'Import failed. Please check backend configuration.';
      setImportResult({ success: false, message: msg });
    } finally {
      setImporting(false);
    }
  };

  const readFileText = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error(`Failed to read ${file.name}`));
    reader.readAsText(file);
  });

  const handleImportFromLocalJson = async () => {
    if (!isoFile && !isccFile) {
      showAlert('warning', 'Choose at least one JSON file (iso-certificate.json or iscc-certificate.json).');
      return;
    }
    setJsonImporting(true);
    setImportResult(null);
    try {
      const [isoText, isccText] = await Promise.all([
        isoFile ? readFileText(isoFile) : Promise.resolve(null),
        isccFile ? readFileText(isccFile) : Promise.resolve(null),
      ]);
      const isoRows = isoText ? slimIsoJsonRows(isoText) : [];
      const isccRows = isccText ? slimIsccJsonRows(isccText) : [];

      const response = await api.post('/audit/certificates/import-from-json-dump', {
        isoJson: isoRows.length ? JSON.stringify(isoRows) : null,
        isccJson: isccRows.length ? JSON.stringify(isccRows) : null,
      });

      setImportResult({ success: true, ...response.data });
      setShowJsonImportModal(false);
      setIsoFile(null);
      setIsccFile(null);
      fetchCertificates();
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || 'Local JSON import failed.';
      setImportResult({ success: false, message: msg });
    } finally {
      setJsonImporting(false);
    }
  };

  if (capabilitiesLoading) {
    return (
      <div className="container-xl py-5 text-center text-muted">
        <Loader className="icon icon-spin mx-auto mb-2" />
        Loading certificate monitoring...
      </div>
    );
  }

  if (!canViewPage) {
    return <Navigate to="/dashboard" replace />;
  }

  /* ── Certificate preview modal ───────────────────────────────────────────── */
  const certPreviewData = selectedCert ? {
    id: selectedCert.id,
    certificateNumber: buildCertificateNumber(selectedCert),
    clientName: buildClientName(selectedCert),
    applicableStandards: buildStandardLabel(selectedCert),
    issueDate: selectedCert.issueDate,
    expiredDate: selectedCert.expiryDate,
    projectCode: selectedCert.projectCode,
  } : null;

  return (
    <>
    {/* Certificate Preview Overlay */}
    {selectedCert && (
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.65)', overflowY: 'auto',
        }}
        onClick={(e) => { if (e.target === e.currentTarget) setSelectedCert(null); }}
      >
        <div style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setSelectedCert(null)}
            style={{
              position: 'fixed', top: 16, right: 16, zIndex: 10000,
              background: '#fff', border: 'none', borderRadius: '50%',
              width: 40, height: 40, display: 'flex', alignItems: 'center',
              justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,.3)',
            }}
          >
            <X size={18} />
          </button>
          <CertificatePreview
            data={certPreviewData}
            onClose={() => setSelectedCert(null)}
            onIssue={
              selectedCert.auditId
                ? () => navigate(`/projects/detail/${selectedCert.leadId || selectedCert.auditId}`)
                : undefined
            }
          />
        </div>
      </div>
    )}
    <div className="container-xl">
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      {/* AuditQ Import Confirmation Modal */}
      {showImportConfirm && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowImportConfirm(false); }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <Upload size={18} className="me-2 text-primary" />
                  Import Certificates from AuditQ
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowImportConfirm(false)} />
              </div>
              <div className="modal-body">
                <p className="mb-2">This will import all approved certificates from <strong>AuditQ production</strong> into the CRM.</p>
                <ul className="mb-0 text-muted small">
                  <li>Certificates already imported (by ID or number) will be <strong>skipped</strong> — no duplicates.</li>
                  <li>Client names will be matched to existing CRM companies where possible.</li>
                  <li>This operation is read-only on AuditQ — no data will be modified there.</li>
                </ul>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={() => setShowImportConfirm(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleImportFromAuditq}>
                  <Upload size={15} className="me-1" />
                  Start Import
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Migrate Local JSON Dumps Modal */}
      {showJsonImportModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={(e) => { if (e.target === e.currentTarget && !jsonImporting) setShowJsonImportModal(false); }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <Upload size={18} className="me-2 text-primary" />
                  Migrate Certificates from Local JSON Files
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowJsonImportModal(false)} disabled={jsonImporting} />
              </div>
              <div className="modal-body">
                <p className="mb-2">
                  Choose the auditqv2-main local dumps: <code>public/iso-certificate.json</code> and{' '}
                  <code>public/iscc-certificate.json</code>. Either file may be left empty.
                </p>
                <ul className="mb-3 text-muted small">
                  <li>Certificates already imported (by AuditQ ID or number) will be <strong>updated, not duplicated</strong>.</li>
                  <li>Only approved certificates (<code>is_approve = 1</code>) are imported.</li>
                  <li>Only Issue Date / Expiry Date and client/standard fields are read from the files — nothing is uploaded except that.</li>
                </ul>
                <div className="mb-3">
                  <label className="form-label">iso-certificate.json</label>
                  <input
                    type="file"
                    accept=".json,application/json"
                    className="form-control"
                    disabled={jsonImporting}
                    onChange={(e) => setIsoFile(e.target.files?.[0] || null)}
                  />
                </div>
                <div>
                  <label className="form-label">iscc-certificate.json</label>
                  <input
                    type="file"
                    accept=".json,application/json"
                    className="form-control"
                    disabled={jsonImporting}
                    onChange={(e) => setIsccFile(e.target.files?.[0] || null)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setShowJsonImportModal(false)}
                  disabled={jsonImporting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleImportFromLocalJson}
                  disabled={jsonImporting || (!isoFile && !isccFile)}
                >
                  {jsonImporting
                    ? <><Loader className="icon icon-spin me-1" size={16} />Migrating...</>
                    : <><Upload size={15} className="me-1" />Start Migration</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Result Card */}
      {importResult && (
        <div className={`alert ${importResult.success ? 'alert-success' : 'alert-danger'} alert-dismissible mb-3`} role="alert">
          {importResult.success ? (
            <div>
              <strong>Import complete.</strong>
              {' '}
              <span className="badge bg-success me-1">{importResult.imported} imported</span>
              <span className="badge bg-secondary me-1">{importResult.skipped} skipped</span>
              {importResult.errors > 0 && <span className="badge bg-warning text-dark">{importResult.errors} errors</span>}
              {importResult.errorList?.length > 0 && (
                <details className="mt-2">
                  <summary className="small text-muted" style={{ cursor: 'pointer' }}>Show errors ({importResult.errorList.length})</summary>
                  <ul className="small mt-1 mb-0">
                    {importResult.errorList.slice(0, 10).map((e, i) => <li key={i}>{e}</li>)}
                    {importResult.errorList.length > 10 && <li>...and {importResult.errorList.length - 10} more</li>}
                  </ul>
                </details>
              )}
            </div>
          ) : (
            <div><strong>Import failed.</strong> {importResult.message}</div>
          )}
          <button type="button" className="btn-close" onClick={() => setImportResult(null)} />
        </div>
      )}

      <div className="row g-2 align-items-center mb-3">
        <div className="col">
          <h2 className="page-title mb-1">Certificate Monitoring</h2>
          <div className="text-muted">Track certificate validity, renewal urgency, and project follow-up.</div>
        </div>
        <div className="col-auto d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={fetchCertificates}
            disabled={loading || importing}
          >
            {loading ? <Loader className="icon icon-spin" size={16} /> : <RefreshCcw size={16} className="me-1" />}
            Refresh
          </button>
          {isAdmin && (
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => setShowImportConfirm(true)}
              disabled={importing || loading}
              title={auditqConfigured === false
                ? 'AuditQ datasource not configured on this server. Add AUDITQ_DB_URL, AUDITQ_DB_USER, AUDITQ_DB_PASS to CLOUD_PROPERTIES secret.'
                : 'Import certificates from AuditQ production database'}
            >
              {importing
                ? <><Loader className="icon icon-spin me-1" size={16} />Importing...</>
                : <><Upload size={16} className="me-1" />Import from AuditQ</>}
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => setShowJsonImportModal(true)}
              disabled={jsonImporting || loading}
              title="Migrate certificates from the local auditqv2-main iso-certificate.json / iscc-certificate.json dumps"
            >
              {jsonImporting
                ? <><Loader className="icon icon-spin me-1" size={16} />Migrating...</>
                : <><Upload size={16} className="me-1" />Migrate Local Files</>}
            </button>
          )}
        </div>
      </div>

      <CertificateLifecycleHealthCard
        stats={healthStats}
        certificates={certificates}
        title="Certificate Health & Lifecycle Status"
        activeStandard={appliedFilters.standard}
        activeLifecycleStatus={appliedFilters.lifecycleStatus}
        onLifecycleTileClick={handleLifecycleTileClick}
      />

      <div className="card" ref={tableSectionRef}>
        <div className="card-body border-bottom">
          <div className="row g-3 align-items-end">
            <div className="col-12 col-lg">
              <label className="form-label">Search</label>
              <div className="input-group">
                <span className="input-group-text bg-white">
                  <Search size={16} className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  value={filters.search}
                  placeholder="Client name or certificate number"
                  onChange={(event) => handleFilterChange('search', event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') handleFilterApply();
                  }}
                />
              </div>
            </div>

            <div className="col-12 col-lg-auto">
              <div className="d-flex flex-wrap gap-2 justify-content-lg-end">
                <FilterButton offcanvasId="offcanvasCertMonitor" activeCount={activeCount} />
                <button
                  type="button"
                  className="btn btn-outline-success"
                  onClick={() => handleExportData('excel')}
                  disabled={loading || exporting !== ''}
                >
                  {exporting === 'excel' ? <Loader className="icon icon-spin me-1" size={16} /> : <Download size={16} className="me-1" />}
                  Export Excel
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={() => handleExportData('pdf')}
                  disabled={loading || exporting !== ''}
                >
                  {exporting === 'pdf' ? <Loader className="icon icon-spin me-1" size={16} /> : <Download size={16} className="me-1" />}
                  Export PDF
                </button>
              </div>
            </div>
          </div>
        </div>

        <FilterAwarenessBar
          title="Certificate Monitoring is showing a filtered view"
          summary={`Filtered by ${activeCount} ${activeCount === 1 ? 'criterion' : 'criteria'}`}
          detail={filterTags.map((tag) => tag.label).join(' · ')}
          loading={loading}
          activeCount={activeCount}
          filterTags={filterTags}
          onRemoveFilter={handleRemoveFilter}
          onResetFilters={handleFilterReset}
        />

        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 72 }}>No</th>
                <th>Certificate Number</th>
                <th>Client Name</th>
                <th>Standard</th>
                <th>Issue Date</th>
                <th>Expiry Date</th>
                <th>Lifecycle Status</th>
                <th>Health Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center py-5 text-muted">
                    <Loader className="icon icon-spin mx-auto mb-2 d-block" />
                    Loading certificates...
                  </td>
                </tr>
              ) : certificates.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-5 text-muted">
                    No certificates found for the selected filters.
                  </td>
                </tr>
              ) : certificates.map((certificate, index) => {
                const statusMeta = resolveStatusMeta(certificate);
                const lifecycleMeta = resolveLifecycleMeta(certificate);
                const detailId = resolveProjectDetailId(certificate);
                const clientDetailId = resolveClientDetailId(certificate);
                const rowNumber = ((pagination.currentPage - 1) * pagination.pageSize) + index + 1;

                return (
                  <tr
                    key={certificate.id || `${buildCertificateNumber(certificate)}-${rowNumber}`}
                    onClick={() => detailId && navigate(`/projects/detail/${detailId}`)}
                    style={{ cursor: detailId ? 'pointer' : 'default' }}
                  >
                    <td>{rowNumber}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-link p-0 fw-semibold text-primary text-start"
                        style={{ textDecoration: 'none' }}
                        onClick={(e) => { e.stopPropagation(); setSelectedCert(certificate); }}
                      >
                        {buildCertificateNumber(certificate)}
                      </button>
                    </td>
                    <td>
                      {clientDetailId ? (
                        <button
                          type="button"
                          className="btn btn-link p-0 fw-semibold text-primary text-start"
                          style={{ textDecoration: 'none' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/audit/clients/${clientDetailId}`);
                          }}
                        >
                          {buildClientName(certificate)}
                        </button>
                      ) : (
                        buildClientName(certificate)
                      )}
                    </td>
                    <td>{buildStandardLabel(certificate)}</td>
                    <td>{formatDate(certificate.issueDate || certificate.validFrom || certificate.createdAt)}</td>
                    <td>{formatDate(certificate.expiryDate || certificate.expiredDate || certificate.validTo)}</td>
                    <td>
                      <span
                        style={{
                          ...statusMeta.badgeStyle,
                          backgroundColor: lifecycleMeta.color,
                        }}
                      >
                        {lifecycleMeta.label}
                      </span>
                    </td>
                    <td>
                      <span style={statusMeta.badgeStyle}>{statusMeta.label}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="card-footer d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
          <div className="text-muted small">
            Showing {certificates.length === 0 ? 0 : ((pagination.currentPage - 1) * pagination.pageSize) + 1}
            –{Math.min(pagination.currentPage * pagination.pageSize, pagination.totalElements)} of {pagination.totalElements} certificates
          </div>

          <div className="d-flex align-items-center gap-3 ms-md-auto">
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Rows</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 88 }}
                value={pagination.pageSize}
                onChange={(event) => {
                  const pageSize = Number(event.target.value);
                  setPagination((prev) => ({ ...prev, pageSize, currentPage: 1 }));
                  persistState({ filters: appliedFilters, search: '', page: 0, size: pageSize });
                }}
              >
                {PAGE_SIZE_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <nav aria-label="Certificate monitoring pagination">
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${pagination.currentPage === 1 ? 'disabled' : ''}`}>
                  <button
                    type="button"
                    className="page-link"
                    onClick={() => { setPagination((prev) => { persistState({ filters: appliedFilters, search: '', page: prev.currentPage - 2, size: pagination.pageSize }); return { ...prev, currentPage: prev.currentPage - 1 }; }); }}
                    disabled={pagination.currentPage === 1}
                  >
                    Previous
                  </button>
                </li>
                {pageItems.map((page) => (
                  typeof page === 'number' ? (
                    <li key={page} className={`page-item ${pagination.currentPage === page ? 'active' : ''}`}>
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => { persistState({ filters: appliedFilters, search: '', page: page - 1, size: pagination.pageSize }); setPagination((prev) => ({ ...prev, currentPage: page })); }}
                      >
                        {page}
                      </button>
                    </li>
                  ) : (
                    <li key={page} className="page-item disabled"><span className="page-link">…</span></li>
                  )
                ))}
                <li className={`page-item ${pagination.currentPage === pagination.totalPages ? 'disabled' : ''}`}>
                  <button
                    type="button"
                    className="page-link"
                    onClick={() => { setPagination((prev) => { persistState({ filters: appliedFilters, search: '', page: prev.currentPage, size: pagination.pageSize }); return { ...prev, currentPage: prev.currentPage + 1 }; }); }}
                    disabled={pagination.currentPage === pagination.totalPages}
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>

      <AdvancedFilterPanel
        offcanvasId="offcanvasCertMonitor"
        title="Filter Certificates"
        filterData={filters}
        onChange={handleFilterChange}
        onApply={handleFilterApply}
        onReset={handleFilterReset}
        filterFields={filterFields}
        showPeriod={true}
      />
    </div>
    </>
  );
};

export default CertificateMonitoring;
